import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function KalkBayPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (typeof document === 'undefined') return;

    try {
      document.title = 'Kalk Bay Painters Cape Town | Coastal Village Painting Services | Heritage Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Expert painting services in Kalk Bay, Cape Town. Specialist coastal painting for historic cottages, fishing village homes, and seaside properties. Marine-grade protection and vibrant colors.');
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'Kalk Bay painters, coastal painting Cape Town, heritage cottage painting');
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kalk-bay`);
      }
    } catch (err) {
      console.error('Error setting up meta tags:', err);
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/142cda2de04e8f50749929ddf984bc64.jpg"
            alt="Professional Painting Services in Kalk Bay"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Kalk Bay Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Expert coastal painting for Kalk Bay's historic cottages, fishing village homes, and seaside properties with vibrant colors and marine-grade protection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
                Get Free Quote
              </Link>
              <Link to="/services" className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200">
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Specialized Painting for Kalk Bay's Unique Coastal Character
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Kalk Bay stands as one of Cape Town's most charming and authentic coastal villages, renowned for its colorful Victorian cottages, historic fishing harbor, vibrant artistic community, and bohemian seaside atmosphere. This unique neighborhood requires painting services that understand and celebrate its distinctive character while providing robust protection against the challenging marine environment that defines this beloved False Bay location.
              </p>
              <p>
                Our specialized painting services in Kalk Bay honor the area's rich heritage and artistic spirit while delivering modern protection and durability. We work with homeowners, business owners, and property managers to preserve the village's iconic colorful aesthetic, restore historic architectural details, and protect properties from salt air, ocean spray, and coastal weather conditions that make Kalk Bay both beautiful and demanding for exterior surfaces.
              </p>
              <p>
                Working in Kalk Bay requires sensitivity to heritage preservation, understanding of coastal painting challenges, and appreciation for the community's creative character. Our experienced team has completed numerous projects throughout this historic fishing village, from Victorian cottages and fisherman's homes to galleries, restaurants, and commercial properties, earning trust through quality workmanship and respect for Kalk Bay's unique identity.
              </p>
              <p>
                From vibrant exterior color restoration to interior cottage renovations and commercial property maintenance, we provide comprehensive painting solutions that enhance Kalk Bay's charm, protect valuable investments, and contribute to the village's distinctive visual appeal. Our commitment to excellence ensures every property receives expert care that balances aesthetic beauty with practical durability in this special coastal environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Kalk Bay Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions celebrating Kalk Bay's colorful character and coastal heritage
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Victorian Cottage Restoration',
                description: 'Expert restoration painting for Kalk Bay\'s historic Victorian cottages with period-appropriate colors, heritage detail work, and authentic finishes that preserve architectural character.',
                features: ['Heritage color matching', 'Architectural detail restoration', 'Period-appropriate techniques', 'Character preservation', 'Historic authenticity', 'Expert craftsmanship']
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Coastal Protection Painting',
                description: 'Marine-grade exterior painting systems designed specifically for Kalk Bay\'s harsh coastal environment with salt-resistant and weather-proof coatings.',
                features: ['Salt-resistant coatings', 'Marine-grade protection', 'UV-stable pigments', 'Moisture management', 'Weather sealing', 'Long-term durability']
              },
              {
                icon: 'ri-palette-line',
                title: 'Colorful Exterior Painting',
                description: 'Vibrant exterior painting celebrating Kalk Bay\'s iconic colorful character with bold, beautiful colors that enhance the village\'s artistic atmosphere.',
                features: ['Bold color selection', 'Fade-resistant finishes', 'Creative color consultation', 'Village aesthetic harmony', 'Premium pigments', 'Color retention']
              },
              {
                icon: 'ri-store-2-line',
                title: 'Commercial Property Painting',
                description: 'Professional painting for Kalk Bay\'s restaurants, galleries, shops, and businesses with minimal disruption and attractive results that draw customers.',
                features: ['Flexible scheduling', 'Quick turnaround', 'Business-friendly approach', 'Attractive finishes', 'Minimal disruption', 'Customer appeal']
              },
              {
                icon: 'ri-home-heart-line',
                title: 'Interior Cottage Painting',
                description: 'Charming interior painting for cottage homes with cozy colors, creative finishes, and attention to the unique layouts of historic properties.',
                features: ['Cottage-style colors', 'Creative techniques', 'Small space expertise', 'Character enhancement', 'Cozy atmospheres', 'Detail work']
              },
              {
                icon: 'ri-tools-line',
                title: 'Fishing Village Maintenance',
                description: 'Ongoing maintenance programs for Kalk Bay properties with regular inspections, preventive care, and touch-ups to combat coastal wear.',
                features: ['Regular maintenance', 'Coastal wear prevention', 'Priority service', 'Long-term protection', 'Preventive care', 'Touch-up services']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-yellow-400 text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Understanding Kalk Bay's Coastal Painting Challenges
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Marine Environment</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kalk Bay's position directly on False Bay exposes properties to constant salt air, ocean spray, high humidity, and strong coastal winds that accelerate paint deterioration and surface damage. Our marine-grade painting systems utilize specialized salt-resistant primers, breathable topcoats, and corrosion-inhibiting treatments that protect surfaces from coastal aggression while maintaining beautiful appearances.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We understand the unique challenges of painting in a working fishing village environment and employ advanced coating technologies, proper surface preparation, and strategic application timing that ensures maximum durability and longevity despite Kalk Bay's demanding coastal conditions and maritime exposure.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Heritage Preservation</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Many Kalk Bay properties are historic Victorian cottages and heritage buildings requiring sensitive restoration approaches that preserve architectural character, original details, and period authenticity. Our heritage painting expertise includes research into historical color palettes, traditional application techniques, and careful restoration of decorative elements that maintain the village's historic charm.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We work closely with homeowners and heritage consultants to ensure restoration projects honor original architectural intent while incorporating modern protective coatings that extend the life of historic surfaces without compromising aesthetic authenticity or period character throughout Kalk Bay's treasured historic properties.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Colorful Character</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kalk Bay is famous for its vibrant, colorful buildings that create the village's distinctive visual identity and artistic atmosphere. Maintaining and enhancing this colorful character requires expert color selection, fade-resistant pigments, and quality application techniques that ensure bold, beautiful colors remain vibrant despite intense UV exposure and coastal conditions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our color consultation services help property owners select colors that complement the village aesthetic, express individual creativity, and utilize premium fade-resistant formulations that keep Kalk Bay's iconic colorful charm looking fresh and vibrant for years while contributing to the community's beloved bohemian character.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Access Challenges</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kalk Bay's narrow streets, hillside locations, and compact property layouts often present access challenges for painting projects. Our experienced team is skilled in working within tight spaces, utilizing specialized equipment for difficult access areas, and coordinating logistics in the village's unique urban environment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We plan projects carefully to minimize disruption to neighbors, coordinate with local traffic patterns, and employ efficient work practices that respect the village's residential character while delivering professional results despite the logistical challenges inherent in Kalk Bay's charming but compact coastal setting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Kalk Bay Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for coastal village properties
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Property Assessment',
                description: 'Thorough on-site evaluation of your Kalk Bay property including coastal exposure assessment, heritage considerations, and detailed discussion of your vision and requirements.'
              },
              {
                step: '02',
                title: 'Color & Design Consultation',
                description: 'Expert color consultation respecting Kalk Bay\'s character with recommendations for fade-resistant colors, heritage-appropriate palettes, and creative options that enhance your property.'
              },
              {
                step: '03',
                title: 'Detailed Proposal',
                description: 'Comprehensive quotation outlining scope, specialized coastal materials, timeline, and transparent pricing with no hidden costs or surprises throughout your project.'
              },
              {
                step: '04',
                title: 'Surface Preparation',
                description: 'Meticulous preparation including salt removal, surface repairs, moisture treatment, and proper priming to ensure optimal adhesion and long-lasting results in coastal conditions.'
              },
              {
                step: '05',
                title: 'Expert Application',
                description: 'Professional painting using marine-grade materials, proven coastal techniques, and experienced craftsmen who understand Kalk Bay\'s unique environmental challenges and aesthetic character.'
              },
              {
                step: '06',
                title: 'Quality Inspection',
                description: 'Thorough final inspection, detailed touch-ups, and complete walkthrough ensuring every aspect meets our exacting standards and your complete satisfaction with the finished results.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl font-bold text-yellow-400 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Kalk Bay Property Owners Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-ship-line',
                title: 'Coastal Expertise',
                description: 'Specialized knowledge and experience in coastal painting challenges, marine-grade materials, and proven techniques that protect Kalk Bay properties from harsh seaside conditions and salt air exposure.'
              },
              {
                icon: 'ri-building-line',
                title: 'Heritage Sensitivity',
                description: 'Deep respect for Kalk Bay\'s historic character with expertise in Victorian cottage restoration, period-appropriate colors, and preservation techniques that honor the village\'s architectural heritage.'
              },
              {
                icon: 'ri-palette-line',
                title: 'Creative Color Solutions',
                description: 'Expert color consultation that celebrates Kalk Bay\'s vibrant character while ensuring fade-resistant, long-lasting results that maintain the village\'s iconic colorful charm and artistic atmosphere.'
              },
              {
                icon: 'ri-community-line',
                title: 'Local Understanding',
                description: 'Familiarity with Kalk Bay\'s unique community, access challenges, and village character with respectful work practices that minimize disruption and honor the area\'s special residential atmosphere.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Quality Guarantee',
                description: 'Commitment to exceptional quality using premium marine-grade materials, experienced craftsmen, and rigorous quality control that ensures lasting results worthy of your Kalk Bay property investment.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Professional Service',
                description: 'Reliable, courteous, and professional service from initial consultation through project completion with clear communication, respect for your property, and dedication to your complete satisfaction.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Recent Kalk Bay Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our coastal painting projects celebrating Kalk Bay's unique character
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Historic Victorian Cottage',
                location: 'Kalk Bay Main Road',
                description: 'Complete exterior restoration of a charming Victorian cottage featuring heritage color research, architectural detail restoration, and marine-grade protective coatings that preserved the property\'s historic character while ensuring long-term coastal durability.',
                image: 'https://readdy.ai/api/search-image?query=Restored%20Victorian%20cottage%20in%20Kalk%20Bay%20Cape%20Town%20with%20vibrant%20colorful%20paint%2C%20historic%20architecture%2C%20coastal%20setting%2C%20charming%20details%2C%20professional%20restoration%2C%20seaside%20village%20atmosphere%2C%20beautiful%20finish&width=800&height=600&seq=kalkbay-project-001&orientation=landscape'
              },
              {
                title: 'Harborside Restaurant',
                location: 'Kalk Bay Harbor',
                description: 'Professional exterior and interior painting for a popular Kalk Bay restaurant including vibrant coastal colors, weather-resistant finishes, and efficient scheduling that minimized business disruption while creating an inviting atmosphere for customers.',
                image: 'https://readdy.ai/api/search-image?query=Colorful%20restaurant%20building%20in%20Kalk%20Bay%20harbor%20Cape%20Town%2C%20vibrant%20paint%2C%20coastal%20setting%2C%20fishing%20boats%20nearby%2C%20attractive%20exterior%2C%20professional%20finish%2C%20seaside%20dining%20atmosphere&width=800&height=600&seq=kalkbay-project-002&orientation=landscape'
              },
              {
                title: 'Fisherman\'s Cottage Complex',
                location: 'Kalk Bay Coastal Area',
                description: 'Comprehensive painting project for a row of traditional fisherman\'s cottages featuring coordinated colorful exteriors, salt-resistant coatings, and careful attention to preserving the authentic fishing village character that makes Kalk Bay special.',
                image: 'https://readdy.ai/api/search-image?query=Row%20of%20colorful%20fisherman%20cottages%20in%20Kalk%20Bay%20Cape%20Town%2C%20vibrant%20paint%20colors%2C%20traditional%20architecture%2C%20coastal%20village%20setting%2C%20harbor%20views%2C%20professional%20painting%2C%20authentic%20character&width=800&height=600&seq=kalkbay-project-003&orientation=landscape'
              },
              {
                title: 'Hillside Cottage Renovation',
                location: 'Kalk Bay Heights',
                description: 'Challenging hillside property renovation including exterior painting with specialized access equipment, bold color transformation, and interior cottage painting that created a beautiful modern living space while respecting the property\'s historic charm.',
                image: 'https://readdy.ai/api/search-image?query=Hillside%20cottage%20in%20Kalk%20Bay%20Cape%20Town%20with%20fresh%20colorful%20paint%2C%20mountain%20backdrop%2C%20ocean%20views%2C%20renovated%20exterior%2C%20charming%20architecture%2C%20professional%20finish&width=800&height=600&seq=kalkbay-project-004&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="w-full h-80">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            What Kalk Bay Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Mitchell',
                location: 'Kalk Bay Victorian Cottage',
                text: 'Exceptional restoration work on our historic cottage. The team understood the importance of preserving the character while providing modern protection. The vibrant colors look stunning and have held up beautifully against the coastal weather.'
              },
              {
                name: 'James Peterson',
                location: 'Kalk Bay Restaurant Owner',
                text: 'Professional service with minimal disruption to our business. The fresh paint has attracted more customers and the marine-grade coatings are holding up perfectly despite the harsh harbor environment. Highly recommend!'
              },
              {
                name: 'Linda van der Merwe',
                location: 'Kalk Bay Hillside Home',
                text: 'Outstanding work on our challenging hillside property. The team handled the difficult access with expertise and the results exceeded our expectations. Our cottage has never looked better and fits perfectly with the village character.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">&quot;{testimonial.text}&quot;</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Transform Your Kalk Bay Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us today for a free consultation and discover how our coastal painting expertise can enhance your property's beauty and protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
            <a href="tel:+27123456789" className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}